/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type CompletionStatus } from '../../lib/api/home.js';
import { useCompleteHomeTask, useHomeTasks } from '../../lib/queries/home.js';

import { HomeTaskCard } from './home-task-card.js';
import { HomeTaskForm } from './home-task-form.js';

interface HomeTasksPanelProps {
  readonly clientId: string;
  /** Therapist/Admin can assign; Parent can only mark completion. */
  readonly canAssign: boolean;
}

const DONE: CompletionStatus = 'done';

/** Home-tasks daily view: progress + task cards + (therapist) assign (Figma). */
export function HomeTasksPanel({ clientId, canAssign }: HomeTasksPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tasks = useHomeTasks(clientId);
  const complete = useCompleteHomeTask(clientId);

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onMark = useCallback(
    (id: string) => {
      complete.mutate({ id, payload: { status: DONE } });
    },
    [complete],
  );

  const rows = tasks.data ?? [];
  const doneCount = rows.filter((task) => task.todayStatus === DONE).length;
  const pct = rows.length > 0 ? (doneCount / rows.length) * 100 : 0;

  return (
    <div>
      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {t('parent.homeTasks.dailyProgress')}
          </p>
          <span className="text-sm font-semibold text-slate-800 dark:text-white">
            {t('parent.homeTasks.completedCount', { done: doneCount, total: rows.length })}
          </span>
        </div>
        <ProgressBar value={pct} />
      </div>

      {canAssign && (
        <div className="mb-4 flex justify-end">
          <Button onClick={openModal}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('home.assign')}
          </Button>
        </div>
      )}

      <QueryState
        isLoading={tasks.isLoading}
        isError={tasks.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.homeTasks.empty')}
      >
        <div className="space-y-3">
          {rows.map((task) => (
            <HomeTaskCard key={task.id} task={task} canMark={!canAssign} onMark={onMark} />
          ))}
        </div>
      </QueryState>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('home.assign')}
        description={t('home.assignSubtitle')}
        closeLabel={t('common.cancel')}
      >
        <HomeTaskForm clientId={clientId} onDone={closeModal} />
      </Modal>
    </div>
  );
}
